import type { ReactNode } from 'react'

import type { Booking, BookingSource } from '../../api/bookings.ts'
import { useAuth } from '../../auth/AuthProvider.tsx'
import { PANEL_HAIRLINE } from '../webhooks/Panel.tsx'

import { BookingHooks } from './BookingHooks.tsx'
import { BookingRequestPanel } from './BookingRequest.tsx'
import { itemDims, itemWeight, referenceSummary } from './format.ts'

// The expanded row: the design's two-pane detail — the originating request and
// items on the left, identifiers and webhook history on the right.

// What a row is, by where the service read it. A dispatch row is a payload no
// one has booked yet: it has no delivery, and the request console's read looks
// only in the shared store, so it would answer not found.
const ROW_KIND = {
  delivery: { idLabel: 'Delivery ID', isPayload: false, hasBookingRequest: true },
  payload: { idLabel: 'Payload ID', isPayload: true, hasBookingRequest: true },
  dispatch: { idLabel: 'Payload ID', isPayload: true, hasBookingRequest: false },
} as const satisfies Record<
  BookingSource,
  { idLabel: string; isPayload: boolean; hasBookingRequest: boolean }
>

const HEADING = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--curri-text-tertiary)',
} as const

const ItemsCard = ({ items }: { items: Booking['items'] }) => (
  <div
    style={{
      border: '1px solid var(--curri-border)',
      borderRadius: 12,
      padding: '14px 16px 16px',
    }}
  >
    <div style={HEADING}>Items</div>
    {items.length === 0 ? (
      <p
        style={{
          margin: '10px 0 0',
          fontSize: 12.5,
          color: 'var(--curri-text-tertiary)',
        }}
      >
        No items on record.
      </p>
    ) : (
      items.map((item, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 12,
            padding: '9px 0 0',
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 13,
              color: 'var(--curri-text)',
            }}
          >
            {/* A stored piece count above one already rides in the
                description as `(x12)` — the wire carries no quantity field. */}
            {item.description || 'Unnamed item'}
          </span>
          <span
            style={{
              flex: 'none',
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 11.5,
              color: 'var(--curri-text-tertiary)',
            }}
          >
            {itemDims(item)}
          </span>
          <span
            style={{
              flex: 'none',
              width: 74,
              textAlign: 'right',
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 11.5,
              color: 'var(--curri-text-tertiary)',
            }}
          >
            {itemWeight(item)}
          </span>
        </div>
      ))
    )}
  </div>
)

const IdentifierRow = ({
  label,
  value,
}: {
  label: string
  value: ReactNode
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 12,
      padding: '9px 0',
      borderBottom: `1px solid ${PANEL_HAIRLINE}`,
    }}
  >
    <span
      style={{
        flex: 'none',
        width: 96,
        fontSize: 12.5,
        color: 'var(--curri-text-tertiary)',
      }}
    >
      {label}
    </span>
    <span
      style={{
        flex: 1,
        minWidth: 0,
        fontFamily: 'var(--curri-font-mono)',
        fontSize: 11.5,
        color: 'var(--curri-text-secondary)',
        overflowWrap: 'anywhere',
      }}
    >
      {value}
    </span>
  </div>
)

// Everything a partner needs to reconcile this row against their own system.
// Only rows the API actually returned: an unbacked field ("Created via", the
// request latency) is omitted, never dashed in as if it were data.
const IdentifiersCard = ({ booking }: { booking: Booking }) => (
  <div
    style={{
      border: '1px solid var(--curri-border)',
      borderRadius: 12,
      padding: '14px 16px 6px',
    }}
  >
    <div style={HEADING}>Identifiers</div>
    <IdentifierRow
      label={ROW_KIND[booking.source].idLabel}
      value={<span style={{ color: 'var(--curri-text)' }}>{booking.id}</span>}
    />
    {ROW_KIND[booking.source].isPayload && (
      <IdentifierRow
        label="Delivery"
        value={booking.deliveryExternalId ?? 'Not booked yet'}
      />
    )}
    {Object.keys(booking.referenceNumbers).length > 0 && (
      <IdentifierRow
        label="References"
        value={referenceSummary(booking)}
      />
    )}
    {booking.customerData !== undefined && (
      <IdentifierRow
        label="customerData"
        value={JSON.stringify(booking.customerData)}
      />
    )}
    <IdentifierRow label="Account" value={booking.accountId ?? '—'} />
    {booking.vehicle !== null && (
      <IdentifierRow label="Vehicle" value={booking.vehicle} />
    )}
    <IdentifierRow label="Mode" value={booking.isSandbox ? 'Sandbox' : 'Production'} />
  </div>
)

export const BookingDetail = ({ booking }: { booking: Booking }) => {
  // The request console reads Datadog through the service, which answers
  // Curri admins only for now (INT-488) — so a partner's expanded row has no
  // console rather than one that would always say it could not load.
  const { state } = useAuth()
  const isCurriAdmin =
    state.status === 'authenticated' && state.caller.isCurriAdmin
  const hasRequestConsole =
    isCurriAdmin && ROW_KIND[booking.source].hasBookingRequest

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        // The left inset (30px chevron column + 12px gap) tucks the detail under
        // the row's content rather than its affordance.
        padding: '9px 12px 22px 42px',
      }}
    >
      <div
        style={{
          flex: '1.3 1 340px',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {hasRequestConsole && <BookingRequestPanel booking={booking} />}
        <ItemsCard items={booking.items} />
      </div>
      <div
        style={{
          flex: '1 1 280px',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <IdentifiersCard booking={booking} />
        <BookingHooks deliveryExternalId={booking.deliveryExternalId} />
      </div>
    </div>
  )
}
